import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import UseGetDataSearch from "../../../../Components/Hooks/useFetchDataBySearch";
import UseFetchCurrency from "../../../../Components/Hooks/useFetchCurrencyData";
import SearchBar from "../../../../Components/Utils/SearchBar";
import {
  CancelButton,
  CreateButton,
  FilterButton,
  SubmitButton,
} from "../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import ViewToggle from "../../../../Components/Utils/ViewToggle";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import { NuDate } from "../../../../Components/Utils/Input";
import ProformaInvoiceTable from "./ProformaInvoiceTable";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";
import axiosInstance from "../../../../Services/axiosCommon";
import AsyncSelect from "react-select/async";

function ProformaInvoiceIndex() {
  const location = useLocation();
  const currentPage = location.state?.currentPage;
  const searchQuery = location.state?.searchQuery;

  const [page, setPage] = useState(currentPage || 1);
  const [pageCount, setPageCount] = useState(
    Number(localStorage.getItem("perPageView")) || 20
  );
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("proformaInvoiceViewMode") || "table"
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const [filter, setFilter] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  // Async dropdown states for filters
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const effectiveViewMode = isMobile ? "card" : (viewMode === "list" ? "table" : viewMode);

  // Dropdown options
  const { value: currencyOptions } = UseFetchCurrency("/currency/drop-down");

  // Async search loader for customers
  const loadCustomerOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/customers/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => ({
          ...item,
          value: item.id !== undefined ? item.id : item.value,
          label: item.name || item.customerName || item.label || `Customer #${item.id}`,
        }));
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching customer options:", err);
        callback([]);
      });
  }, []);

  // Async search loader for bank accounts
  const loadBankOptions = useCallback((inputValue, callback) => {
    axiosInstance
      .get("/banks/drop-down", {
        params: { search: inputValue ? inputValue.trim() : "" },
      })
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const formatted = raw.map((item) => {
          const label = item.name || (item.bankName ? (item.accountNumber ? `${item.bankName} - ${item.accountNumber}` : item.bankName) : (item.label || item.title || item.code || String(item.id || "")));
          return {
            ...item,
            value: item.id !== undefined ? item.id : item.value,
            label: label || `Bank #${item.id}`,
          };
        });
        callback(formatted);
      })
      .catch((err) => {
        console.error("Error fetching bank options:", err);
        callback([]);
      });
  }, []);

  const asyncSelectStyles = {
    control: (provided) => ({
      ...provided,
      border: "1px solid #e5e5e5",
      outline: "none",
      borderRadius: "10px",
      padding: "1px 0",
      color: "#000",
      textAlign: "left",
      fontWeight: "500",
      width: "100%",
      minHeight: "38px",
      backgroundColor: "#fff",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
    option: (provided, state) => ({
      ...provided,
      padding: "8px 12px",
      fontSize: "12px",
      color: state.isSelected ? "#fff" : "#000",
      backgroundColor: state.isSelected ? "#2563EB" : state.isFocused ? "#eff6ff" : "#fff",
      ":hover": {
        color: "#fff",
        backgroundColor: "#60A5FA",
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "12px",
      color: "#000",
      fontWeight: "500",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "12px",
      color: "#9ca3af",
      fontWeight: "500",
    }),
  };

  const [filterProps] = useState({
    dateFrom: "",
    dateTo: "",
    customerId: "",
    bankId: "",
    currencyId: "",
    vatApplicable: "",
  });

  const [params, setParams] = useState({
    page: page,
    per_page: pageCount,
    search: searchQuery || "",
    dateFrom: "",
    dateTo: "",
    customerId: "",
    bankId: "",
    currencyId: "",
    vatApplicable: "",
  });

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page: page,
      per_page: pageCount,
    }));
  }, [page, pageCount]);

  const clearFilter = () => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      dateFrom: "",
      dateTo: "",
      customerId: "",
      bankId: "",
      currencyId: "",
      vatApplicable: "",
    }));
    setSelectedCustomer(null);
    setSelectedBank(null);
    formik.resetForm();
    setFilter(false);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: filterProps,
    onSubmit: async (values) => {
      setParams((prev) => ({
        ...prev,
        page: 1,
        dateFrom: values.dateFrom || "",
        dateTo: values.dateTo || "",
        customerId: values.customerId || "",
        bankId: values.bankId || "",
        currencyId: values.currencyId || "",
        vatApplicable: values.vatApplicable !== "" ? values.vatApplicable : "",
      }));
      setPage(1);
      setShowFilterDialog(false);
      const isAnyFilterActive = Boolean(
        values.dateFrom ||
        values.dateTo ||
        values.customerId ||
        values.bankId ||
        values.currencyId ||
        (values.vatApplicable !== "" && values.vatApplicable !== undefined)
      );
      setFilter(isAnyFilterActive);
    },
  });

  const { data, totalCount, getData, dataLoading } = UseGetDataSearch(
    "/proforma-invoice/list",
    params
  );

  const canCreate = GetAccess("proforma_invoice_create") || GetRole();

  return (
    <div className="w-full h-full px-2 sm:px-4 py-2">
      {/* Top Header Bar */}
      <div className="w-full flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-3 mb-3">
        <div className="w-full md:w-auto flex justify-between items-center gap-2">
          <Breadcrumb
            items={[
              { name: "Sales", link: "/sales/proforma-invoice" },
              { name: "Proforma Invoice" },
            ]}
          />
          <div className="flex md:hidden items-center gap-2">
            <FilterButton handleClick={() => setShowFilterDialog(true)} />
            {filter && (
              <FilterButton handleClick={() => clearFilter()} label="Clear" />
            )}
            {canCreate && (
              <Link to="/sales/proforma-invoice/create">
                <CreateButton label="Add PI" />
              </Link>
            )}
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-wrap md:flex-nowrap items-center gap-2">
          <div className="w-full md:w-64">
            <SearchBar
              handleChange={(e) => {
                setParams({ ...params, search: e.target.value, page: 1 });
                setPage(1);
              }}
              value={params.search}
              placeholder="Search PI number, customer..."
            />
          </div>

          <div className="hidden md:flex items-center gap-2">
            <FilterButton handleClick={() => setShowFilterDialog(true)} />
            {filter && (
              <FilterButton handleClick={() => clearFilter()} label="Clear" />
            )}
          </div>

          <ViewToggle
            viewMode={effectiveViewMode}
            onViewChange={(mode) => setViewMode(mode)}
            storageKey="proformaInvoiceViewMode"
          />

          <div className="hidden md:flex items-center gap-2">
            {canCreate && (
              <Link to="/sales/proforma-invoice/create">
                <CreateButton label="Add Proforma Invoice" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Table / Card List */}
      <ProformaInvoiceTable
        data={data}
        total={totalCount}
        pageIndex={page}
        setPageIndex={setPage}
        dataCount={pageCount}
        setDataCount={setPageCount}
        searchQuery={params?.search !== "" ? params?.search : ""}
        fetcher={getData}
        dataLoading={dataLoading}
        viewMode={effectiveViewMode}
      />

      {/* Filter Modal Dialog */}
      {showFilterDialog && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs z-[9999] flex justify-center items-center p-3 sm:p-4">
          <div className="animate-scale-in w-[92vw] sm:w-[540px] md:w-[640px] max-w-full max-h-[85vh] bg-white p-5 sm:p-6 rounded-2xl shadow-2xl overflow-y-auto flex flex-col">
            <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                Filter Proforma Invoices
              </h2>
              <button
                onClick={() => setShowFilterDialog(false)}
                type="button"
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row flex-wrap -mx-2">
                {/* Date Range */}
                <div className="w-full px-2 mb-2">
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    Date Range
                  </p>
                  <div className="sm:flex gap-2">
                    <NuDate
                      label="From"
                      placeholder="dd/mm/yyyy"
                      formik={formik}
                      name="dateFrom"
                      width="sm:w-1/2"
                      px="0"
                    />
                    <NuDate
                      label="To"
                      placeholder="dd/mm/yyyy"
                      formik={formik}
                      name="dateTo"
                      width="sm:w-1/2"
                      px="0"
                    />
                  </div>
                </div>

                {/* Customer with Async Search */}
                <div className="w-full sm:w-1/2 px-2 py-2">
                  <label className="text-xs 2xl:text-sm">
                    Customer
                  </label>
                  <div className="mt-1">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadCustomerOptions}
                      value={selectedCustomer}
                      onChange={(opt) => {
                        setSelectedCustomer(opt);
                        formik.setFieldValue("customerId", opt?.value || "");
                      }}
                      placeholder="Select Customer"
                      isClearable={true}
                      className="text-xs 2xl:text-sm mt-2 font-medium"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={asyncSelectStyles}
                    />
                  </div>
                </div>

                {/* Bank with Async Search */}
                <div className="w-full sm:w-1/2 px-2 py-2">
                  <label className="text-xs 2xl:text-sm">
                    Bank
                  </label>
                  <div className="mt-1">
                    <AsyncSelect
                      cacheOptions
                      defaultOptions
                      loadOptions={loadBankOptions}
                      value={selectedBank}
                      onChange={(opt) => {
                        setSelectedBank(opt);
                        formik.setFieldValue("bankId", opt?.value || "");
                      }}
                      placeholder="Select Bank"
                      isClearable={true}
                      className="text-xs 2xl:text-sm mt-2 font-medium"
                      menuPlacement="auto"
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      styles={asyncSelectStyles}
                    />
                  </div>
                </div>

                {/* Currency */}
                <FormikSelectTwo
                  label="Currency"
                  placeholder="Select Currency"
                  Options={currencyOptions}
                  formik={formik}
                  name="currencyId"
                  width="w-full sm:w-1/2"
                  px="2"
                  isClear={true}
                />

                {/* VAT Applicable */}
                <FormikSelectTwo
                  label="VAT Applicable"
                  placeholder="Choose"
                  Options={[
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ]}
                  formik={formik}
                  name="vatApplicable"
                  width="w-full sm:w-1/2"
                  px="2"
                  isClear={true}
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-4 mt-2 border-t border-gray-100">
                <CancelButton handleClick={() => setShowFilterDialog(false)} />
                <SubmitButton name="Apply" />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProformaInvoiceIndex;
